/**
 * Single source of truth for the PostHog event taxonomy.
 *
 * Call sites must never pass a raw event-name string literal — always reference
 * `ANALYTICS_EVENTS`. Payload shapes are documented by `TypedEventProps` below.
 */

export const ANALYTICS_EVENTS = {
  // --- Auth ---
  LOGIN_SUCCEEDED: 'login_succeeded',
  LOGIN_FAILED: 'login_failed',

  // --- Join funnel ---
  MEETING_ID_SUBMITTED: 'meeting_id_submitted',
  MEETING_VALIDATION_FAILED: 'meeting_validation_failed',
  ROOM_OPENED: 'room_opened',
  DEVICE_SETUP_COMPLETED: 'device_setup_completed',
  VIEWMODE_SWITCHED: 'viewmode_switched',

  // --- Stream (3D room) ---
  STREAM_SESSION_CREATED: 'stream_session_created',
  STREAM_FAILED: 'stream_failed',
  STREAM_APP_CRASHED: 'stream_app_crashed',
  STREAM_DEGRADED: 'stream_degraded',

  // --- In-meeting engagement ---
  MIC_TOGGLED: 'mic_toggled',
  CAMERA_TOGGLED: 'camera_toggled',
  SCREENSHARE_STARTED: 'screenshare_started',
  SCREENSHARE_STOPPED: 'screenshare_stopped',
  CHAT_MESSAGE_SENT: 'chat_message_sent',
  REACTION_SENT: 'reaction_sent',
  HAND_RAISED: 'hand_raised',
  HAND_LOWERED: 'hand_lowered',
  RECORDING_STARTED: 'recording_started',
  RECORDING_STOPPED: 'recording_stopped',
  TRANSCRIPTION_STARTED: 'transcription_started',
  TRANSCRIPTION_STOPPED: 'transcription_stopped',

  // --- Meeting exit ---
  MEETING_LEFT: 'meeting_left',
  MEETING_ENDED: 'meeting_ended',
} as const;

/** Union of every valid event-name string. */
export type AnalyticsEventName = (typeof ANALYTICS_EVENTS)[keyof typeof ANALYTICS_EVENTS];

/** Keys of the `ANALYTICS_EVENTS` registry (e.g. `'LOGIN_SUCCEEDED'`). */
export type AnalyticsEventKey = keyof typeof ANALYTICS_EVENTS;

type ViewMode = '2d' | '3d';

/**
 * Payload shape per event.
 *
 * This is a reference/lookup map. `useAnalytics().capture` and `captureEvent`
 * intentionally take the looser `Record<string, unknown>` for props (keeps call
 * sites free of casts); consult this map when adding a call site so payloads
 * stay consistent across the codebase.
 *
 * Events with no payload map to `Record<string, never>`.
 */
export interface TypedEventProps {
  /** `method` omitted = normal password login (every call site before it existed). */
  login_succeeded: { zoneCode?: string; method?: 'password_reset' | 'email_code' };
  login_failed: { reason: string };
  meeting_id_submitted: { meetingId: string };
  meeting_validation_failed: { error: string };
  room_opened: { meetingRole?: string };
  device_setup_completed: { source: string; camera: boolean; microphone: boolean; speaker: boolean };
  viewmode_switched: { from: ViewMode; to: ViewMode; source: string };
  stream_session_created: { streamGroupId: string };
  stream_failed: { reason: string };
  stream_app_crashed: {
    fps: number | null;
    reportedFps: number | null;
    framesDecodedDelta: number | null;
    freezeCount: number | null;
    framesDroppedDelta: number | null;
    packetsLostDelta: number | null;
    bytesReceivedDelta: number | null;
    rttMs: number | null;
    jitterMs: number | null;
    sessionAgeSec: number | null;
    decoderImplementation: string | null;
    tabHidden: boolean;
    secondsFrozen: number;
    consecutiveSamples: number;
  };
  stream_degraded: {
    fps: number | null;
    reportedFps: number | null;
    framesDecodedDelta: number | null;
    freezeCount: number | null;
    framesDroppedDelta: number | null;
    packetsLostDelta: number | null;
    bytesReceivedDelta: number | null;
    rttMs: number | null;
    jitterMs: number | null;
    sessionAgeSec: number | null;
    decoderImplementation: string | null;
    tabHidden: boolean;
    secondsDegraded: number;
    consecutiveSamples: number;
  };
  mic_toggled: { enabled: boolean };
  camera_toggled: { enabled: boolean };
  screenshare_started: Record<string, never>;
  screenshare_stopped: Record<string, never>;
  chat_message_sent: { hasAttachment: boolean; hasMention: boolean };
  reaction_sent: { emoji: string };
  hand_raised: Record<string, never>;
  hand_lowered: Record<string, never>;
  recording_started: Record<string, never>;
  recording_stopped: Record<string, never>;
  transcription_started: { language?: string };
  transcription_stopped: { language?: string };
  meeting_left: Record<string, never>;
  meeting_ended: Record<string, never>;
}
